import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { voucherSign } from "@devengo/shared";

import { Dialog, PeriodPicker } from "../components/period-picker";
import { VoidDialog } from "../components/void-dialog";
import { VoidFilter } from "../components/void-filter";
import {
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  PageTitle,
  numClass,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { api } from "../lib/api";
import {
  decimalCents,
  formatCents,
  formatDate,
  periodName,
  toPesosCents,
  voucherNumber,
} from "../lib/format";
import type { VoucherView } from "../lib/hooks";
import { parsePeriod, periodParam } from "../lib/session";

export interface PurchaseRegisterSearch {
  periodo?: string;
  /** Vigentes when absent (plan 024). */
  estado?: "anulados" | "todos";
  /** Issue dates (AAAA-MM-DD): with both, the compras of every period issued between them. */
  desde?: string;
  hasta?: string;
}

type RegisterRow = VoucherView & { period?: { year: number; month: number } };

interface BulkMoveResult {
  movable: number;
  blocked: { voucherId: string; voucher: string; problem: string }[];
}

/** In pesos and with its sign (an NC subtracts), as the Comprobantes totals; a voided one adds 0. */
const pesos = (v: VoucherView) =>
  v.voided ? 0 : voucherSign(v.typeCode) * toPesosCents(decimalCents(v.total), v.exchangeRate);

/**
 * Registro de compras, as in the previous system (Maxi, 2026-10-21): the period's compras by
 * supplier, to tick several and move them to another period at once, or void them (Martín,
 * 2026-10-22: nothing is deleted).
 */
export function PurchaseRegisterPage({
  companyId,
  search,
}: {
  companyId: string;
  search: PurchaseRegisterSearch;
}) {
  const navigate = useNavigate();
  const { year, month } = parsePeriod(search.periodo);
  const periodo = periodParam(year, month);
  const estado = search.estado ?? "vigentes";
  // By issue date, every period (Mathi, 2026-10-22); otherwise the chosen period's compras.
  const byDate = Boolean(search.desde && search.hasta);
  const vouchers = useQuery({
    // Its own key: Comprobantes caches the same period as a bare list (the walkthrough caught it).
    queryKey: byDate
      ? ["vouchers", companyId, "registro", "fechas", search.desde, search.hasta, estado]
      : ["vouchers", companyId, "registro", periodo, estado],
    queryFn: async (): Promise<{ rows: RegisterRow[]; truncated: boolean }> => {
      if (byDate) {
        const found = await api.get<{ vouchers: RegisterRow[]; truncated: boolean }>(
          `/api/companies/${companyId}/vouchers?direction=compra&issuedFrom=${search.desde}&issuedTo=${search.hasta}&status=${estado}`,
        );
        return { rows: found.vouchers, truncated: found.truncated };
      }
      const rows = await api.get<RegisterRow[]>(
        `/api/companies/${companyId}/periods/${year}/${month}/vouchers?direction=compra&status=${estado}`,
      );
      return { rows, truncated: false };
    },
  });
  const [desde, setDesde] = useState(search.desde ?? "");
  const [hasta, setHasta] = useState(search.hasta ?? "");
  /** Undefined drops that param from the URL. */
  const go = (patch: {
    [K in keyof PurchaseRegisterSearch]?: PurchaseRegisterSearch[K] | undefined;
  }) => {
    setSelected(new Set());
    const next = { ...search, periodo, ...patch };
    void navigate({
      to: "/empresas/$companyId/registro-compras",
      params: { companyId },
      search: Object.fromEntries(
        Object.entries(next).filter(([, v]) => v !== undefined && v !== ""),
      ) as PurchaseRegisterSearch,
    });
  };
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Back and forward change the URL, not the inputs: they follow it, and the ticks go.
  useEffect(() => {
    setDesde(search.desde ?? "");
    setHasta(search.hasta ?? "");
    setSelected(new Set());
  }, [search.desde, search.hasta, search.periodo, search.estado]);
  const [moving, setMoving] = useState(false);
  const [voiding, setVoiding] = useState<"void" | "unvoid" | null>(null);

  const groups = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const digits = needle.replace(/\D/g, "");
    const bySupplier = new Map<string, RegisterRow[]>();
    for (const v of vouchers.data?.rows ?? []) {
      const match =
        !needle ||
        v.counterpart.name.toLowerCase().includes(needle) ||
        (digits !== "" && v.counterpart.docNumber.includes(digits));
      if (!match) continue;
      bySupplier.set(v.counterpart.id, [...(bySupplier.get(v.counterpart.id) ?? []), v]);
    }
    return [...bySupplier.values()].sort((a, b) =>
      (a[0] as VoucherView).counterpart.name.localeCompare(
        (b[0] as VoucherView).counterpart.name,
        "es",
      ),
    );
  }, [vouchers.data, filter]);

  // Only its settlement moves a voucher it generated.
  const movable = (v: RegisterRow) => v.settlementId === null;
  const toggle = (ids: string[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  const shown = groups.flat();
  const chosen = shown.filter((v) => selected.has(v.id));

  return (
    <>
      <PageTitle
        subtitle={
          byDate
            ? `Emitidas del ${formatDate(search.desde as string)} al ${formatDate(search.hasta as string)}, en cualquier período · tildá las que quieras mover o anular`
            : `${periodName(year, month)} · compras por proveedor: tildá las que quieras mover de período o anular`
        }
        actions={
          byDate ? null : (
            <PeriodPicker value={periodo} onChange={(value) => go({ periodo: value })} />
          )
        }
      >
        Registro de compras
      </PageTitle>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input
          aria-label="Proveedor"
          className="max-w-80"
          placeholder="Filtrar por proveedor o CUIT"
          value={filter}
          onChange={(e) => {
            // What is ticked is always in sight: a filter does not keep hidden ones (Codex 022).
            setSelected(new Set());
            setFilter(e.target.value);
          }}
        />
        <form
          className="flex items-center gap-1 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            if (desde && hasta) go({ desde, hasta });
          }}
        >
          <span className="text-brand-muted">Fecha</span>
          <Input
            type="date"
            aria-label="Fecha desde"
            className="w-36"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
          <span className="text-brand-muted">a</span>
          <Input
            type="date"
            aria-label="Fecha hasta"
            className="w-36"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
          <Button type="submit" variant="secondary" disabled={!desde || !hasta || desde > hasta}>
            Buscar
          </Button>
          {byDate ? (
            <Button
              type="button"
              variant="secondary"
              className="whitespace-nowrap"
              onClick={() => {
                setDesde("");
                setHasta("");
                go({ desde: undefined, hasta: undefined });
              }}
            >
              Volver al período
            </Button>
          ) : null}
        </form>
        <VoidFilter
          value={estado}
          onChange={(value) => go({ estado: value === "vigentes" ? undefined : value })}
        />
        <div className="flex-1" />
        <span className="text-sm text-brand-muted">
          {chosen.length === 0
            ? "Ninguno tildado"
            : `${chosen.length} tildado${chosen.length === 1 ? "" : "s"} · ${formatCents(
                chosen.reduce((sum, v) => sum + pesos(v), 0),
              )}`}
        </span>
        {estado !== "anulados" ? (
          <Button disabled={chosen.length === 0} onClick={() => setMoving(true)}>
            Mover a otro período
          </Button>
        ) : null}
        {estado !== "anulados" ? (
          <Button
            variant="danger"
            disabled={chosen.length === 0}
            onClick={() => setVoiding("void")}
          >
            Anular
          </Button>
        ) : null}
        {estado !== "vigentes" ? (
          <Button
            variant="secondary"
            disabled={chosen.length === 0}
            onClick={() => setVoiding("unvoid")}
          >
            Reactivar
          </Button>
        ) : null}
      </div>

      <Card>
        <ErrorBox error={vouchers.error} />
        {vouchers.data?.truncated ? (
          <p className="mb-2 text-sm text-amber-800">
            Hay más de 500 compras entre esas fechas: se muestran las primeras. Acotá las fechas.
          </p>
        ) : null}
        {shown.length === 0 && !vouchers.isLoading ? (
          <p className="text-sm text-brand-muted">
            {filter.trim()
              ? "Ningún proveedor coincide con el filtro."
              : byDate
                ? "No hay compras emitidas entre esas fechas."
                : estado === "anulados"
                  ? `No hay compras anuladas en ${periodName(year, month)}.`
                  : `No hay compras en ${periodName(year, month)}.`}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className={tableClass}>
              <thead>
                <tr>
                  <th className={`${thClass} w-8`}>
                    <input
                      type="checkbox"
                      aria-label="Tildar todas"
                      checked={
                        shown.some(movable) &&
                        shown.filter(movable).every((v) => selected.has(v.id))
                      }
                      onChange={(e) =>
                        toggle(
                          shown.filter(movable).map((v) => v.id),
                          e.target.checked,
                        )
                      }
                    />
                  </th>
                  <th className={thClass}>Fecha</th>
                  <th className={thClass}>Comprobante</th>
                  {byDate ? <th className={thClass}>Período</th> : null}
                  <th className={`${thClass} text-right`}>Total</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => {
                  const first = group[0] as RegisterRow;
                  const ids = group.filter(movable).map((v) => v.id);
                  const all = ids.length > 0 && ids.every((id) => selected.has(id));
                  return [
                    <tr key={`${first.counterpart.id}-head`} className="bg-brand-tint">
                      <td className={tdClass}>
                        <input
                          type="checkbox"
                          aria-label={`Tildar las de ${first.counterpart.name}`}
                          disabled={ids.length === 0}
                          checked={all}
                          onChange={(e) => toggle(ids, e.target.checked)}
                        />
                      </td>
                      <td className={`${tdClass} font-semibold`} colSpan={byDate ? 4 : 3}>
                        {first.counterpart.name}
                        <span className="ml-2 tabular text-xs font-normal text-brand-muted">
                          {first.counterpart.docDisplay}
                        </span>
                      </td>
                    </tr>,
                    ...group.map((v) => (
                      <tr
                        key={v.id}
                        className={`hover:bg-brand-tint/50 ${v.voided ? "text-brand-muted" : ""}`}
                      >
                        <td className={tdClass}>
                          <input
                            type="checkbox"
                            aria-label={`Tildar ${v.typeName} ${voucherNumber(v.pointOfSale, v.number)}`}
                            disabled={!movable(v)}
                            checked={selected.has(v.id)}
                            onChange={(e) => toggle([v.id], e.target.checked)}
                          />
                        </td>
                        <td className={`${tdClass} tabular`}>{formatDate(v.issueDate)}</td>
                        <td className={tdClass}>
                          <span className={v.voided ? "line-through" : ""}>
                            {v.typeName}{" "}
                            <span className="tabular text-brand-muted">
                              {voucherNumber(v.pointOfSale, v.number)}
                            </span>
                          </span>
                          {movable(v) ? null : (
                            <span className="ml-2 text-xs text-brand-muted">
                              de una liquidación: se mueve y se anula desde Liquidaciones
                            </span>
                          )}
                          {v.voided ? (
                            <div className="text-xs text-red-700">
                              Anulado el {formatDate(v.voided.at.slice(0, 10))}: {v.voided.reason}
                            </div>
                          ) : null}
                        </td>
                        {byDate ? (
                          <td className={`${tdClass} tabular`}>
                            {v.period
                              ? `${String(v.period.month).padStart(2, "0")}-${v.period.year}`
                              : "—"}
                          </td>
                        ) : null}
                        <td className={`${tdClass} ${numClass} ${v.voided ? "line-through" : ""}`}>
                          {v.currency !== "ARS" ? `${v.currency} ` : ""}
                          {formatCents(voucherSign(v.typeCode) * decimalCents(v.total))}
                        </td>
                      </tr>
                    )),
                    <tr key={`${first.counterpart.id}-total`}>
                      <td className={tdClass} />
                      <td
                        className={`${tdClass} text-xs text-brand-muted`}
                        colSpan={byDate ? 3 : 2}
                      >
                        {group.length} comprobante{group.length === 1 ? "" : "s"}
                      </td>
                      <td className={`${tdClass} ${numClass} font-semibold`}>
                        {formatCents(group.reduce((sum, v) => sum + pesos(v), 0))}
                      </td>
                    </tr>,
                  ];
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className={tdClass} />
                  <td className={tdClass} colSpan={byDate ? 3 : 2}>
                    {shown.length} comprobante{shown.length === 1 ? "" : "s"} · en pesos
                  </td>
                  <td className={`${tdClass} ${numClass}`}>
                    {formatCents(shown.reduce((sum, v) => sum + pesos(v), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
      {voiding ? (
        <VoidDialog
          companyId={companyId}
          vouchers={chosen}
          action={voiding}
          onClose={() => setVoiding(null)}
          onDone={() => {
            setSelected(new Set());
            setVoiding(null);
          }}
        />
      ) : null}
      {moving ? (
        <MoveManyDialog
          companyId={companyId}
          vouchers={chosen}
          from={byDate ? undefined : { year, month }}
          onClose={() => setMoving(false)}
          onMoved={() => {
            setSelected(new Set());
            setMoving(false);
          }}
        />
      ) : null}
    </>
  );
}

/** Asks the API which can go before moving: the move is all or none. */
function MoveManyDialog({
  companyId,
  vouchers,
  from,
  onClose,
  onMoved,
}: {
  companyId: string;
  vouchers: VoucherView[];
  /** Absent when the list spans periods: then there is no "next month" to suggest. */
  from: { year: number; month: number } | undefined;
  onClose: () => void;
  onMoved: () => void;
}) {
  const client = useQueryClient();
  const next = !from
    ? null
    : from.month === 12
      ? { year: from.year + 1, month: 1 }
      : { ...from, month: from.month + 1 };
  const [target, setTarget] = useState(next ? periodParam(next.year, next.month) : "");
  const [reason, setReason] = useState("");
  const ids = vouchers.map((v) => v.id);
  const body = (voucherIds: string[], dryRun: boolean) => ({
    ...parsePeriod(target),
    voucherIds,
    reason,
    dryRun,
  });
  const preview = useQuery({
    queryKey: ["bulk-move-preview", companyId, target, ids],
    queryFn: () =>
      api.post<BulkMoveResult>(`/api/companies/${companyId}/vouchers/move`, body(ids, true)),
    enabled: /^\d{4}-\d{2}$/.test(target),
    retry: false,
  });
  const blocked = new Set(preview.data?.blocked.map((b) => b.voucherId));
  const going = ids.filter((id) => !blocked.has(id));
  const move = useMutation({
    mutationFn: () => api.post(`/api/companies/${companyId}/vouchers/move`, body(going, false)),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["vouchers", companyId] });
      await client.invalidateQueries({ queryKey: ["periods", companyId] });
      await client.invalidateQueries({ queryKey: ["caja", companyId] });
      onMoved();
    },
  });
  return (
    <Dialog
      title={`Mover ${vouchers.length} comprobante${vouchers.length === 1 ? "" : "s"}`}
      onClose={onClose}
    >
      <div className="flex flex-col gap-3">
        <Field label="Período destino">
          <Input type="month" value={target} onChange={(e) => setTarget(e.target.value)} />
        </Field>
        <Field label="Motivo (opcional)">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej.: diferimos el crédito fiscal"
          />
        </Field>
        <ErrorBox error={preview.error} />
        {preview.data && preview.data.blocked.length > 0 ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
            <p className="font-medium">
              {preview.data.blocked.length === 1
                ? "Este no se puede mover:"
                : `Estos ${preview.data.blocked.length} no se pueden mover:`}
            </p>
            <ul className="mt-1 list-disc pl-5">
              {preview.data.blocked.map((b) => (
                <li key={b.voucherId}>
                  {b.voucher}: {b.problem}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <ErrorBox error={move.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={!preview.data || going.length === 0 || move.isPending}
            onClick={() => move.mutate()}
          >
            {going.length === vouchers.length
              ? `Mover ${going.length}`
              : going.length === 0
                ? "Ninguno se puede mover"
                : `Mover los ${going.length} que se pueden`}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
